import { formatMoney, t } from "@hakili/i18n";
import { Button, Card, Chip, Field, GuardedButton, TEXTE_SECONDAIRE } from "@hakili/ui";
import { ArrowRight, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  parseAmount,
  runEstimate,
  type CapacityEstimateDoor,
  type EstimateOutcome,
  type MoneyRange,
} from "../session/capacity-estimate.js";
import { ReadFailure } from "./read-failure.js";

/**
 * `DEALER-PREQUAL` (round A-D1, D-1031-6 item 3), to the designer's iteration-2 frames (`design/dealer-v2` `e521637b`): **« Combien le client peut-il financer ? », one question, three inputs, one answer.**
 *
 * The layer is `session/capacity-estimate.ts` (bound to the merged contract); this screen draws what that layer says and decides nothing (D-1020-7). **It draws ONLY what the door serves** (the architect's
 * ruling of 1 November 2026): no « Reste à vivre », no percentage label on the bar's mark, no per-car instalment and no verdict word on a car, until the door serves them. The bar is geometry over served
 * figures. The door is a seam: lane AA's door is not on main, so with none wired the answer is the failed read, never a number.
 *
 * The salary is neither stored nor logged here: it lives in this component's state and goes nowhere else.
 */

const DURATIONS = [24, 36, 48, 60] as const;
const DEPOSITS = [10, 15, 20, 30] as const;

const fill = (key: Parameters<typeof t>[0], values: Readonly<Record<string, string>>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.split(`{${name}}`).join(value), t(key));

/** « 157 500 à 180 000 FCFA »: the unit once, as the frame draws it. */
/** The client's band code colours a dot and nothing else: the words are the door's (`effortBandLabel`). */
const BAND_DOT = {
  confortable: "bg-emerald-600",
  acceptable: "bg-amber-500",
  "au-dela": "bg-rose-500",
} as const;

/** The bank's cessible share as the RANGE the door served: « 38 % à 40 % », one figure when the ends are equal. Served numbers, formatted by the catalogue's own keys. */
const pctRange = (range: { readonly min: number; readonly max: number }): string =>
  range.min === range.max
    ? fill("dealer.estimation.pourcent", { n: String(range.min) })
    : `${fill("dealer.estimation.pourcent", { n: String(range.min) })} ${t("dealer.estimation.a")} ${fill("dealer.estimation.pourcent", { n: String(range.max) })}`;

const stripUnit = (money: string): string => money.replace(/[\s\u00a0\u202f]FCFA$/u, "");
const rangeWords = (range: MoneyRange): string =>
  `${stripUnit(range.min)} ${t("dealer.estimation.a")} ${range.max}`;

export interface EstimationProps {
  /** Lane AA's door, through the session; absent until it stands. */
  readonly door: CapacityEstimateDoor | undefined;
  /** Opens a park car's own page. */
  readonly onOpenVehicle: (vehicleId: string) => void;
}

/**
 * A short list of exclusive choices as ONE joined row (the frame's `.seg`; the designer's change on the 1440 frame: it never wraps). **Not the kit's `SegmentedControl`**: that one is skinned by the BANK's theme (`bank-theme.css`), which this app does not load, so its
 * chosen segment draws as nothing here. Pressed state is `aria-pressed`, as the kit's own is.
 */
function Choice(props: {
  readonly label: string;
  readonly value: number;
  readonly options: readonly { readonly value: number; readonly label: string; readonly testId: string }[];
  readonly onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <div
      role="group"
      aria-label={props.label}
      className="flex w-full overflow-hidden rounded-xl border border-gray-200 bg-white"
    >
      {props.options.map((option, index) => {
        const pressed = option.value === props.value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            data-testid={option.testId}
            onClick={() => {
              props.onChange(option.value);
            }}
            className={`flex-1 whitespace-nowrap px-2 py-2 text-center text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400 ${
              index === 0 ? "" : "border-l border-gray-200"
            } ${pressed ? "bg-indigo-600 text-white" : "bg-white text-gray-700 hover:bg-gray-50"}`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const unwired: CapacityEstimateDoor = () => Promise.reject(new Error("capacity-estimate: no door is wired"));

export function Estimation({ door, onOpenVehicle }: EstimationProps): React.JSX.Element {
  const [net, setNet] = useState("");
  const [commitments, setCommitments] = useState("");
  const [duration, setDuration] = useState(36);
  const [deposit, setDeposit] = useState(15);
  const [outcome, setOutcome] = useState<EstimateOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const salaryRef = useRef<HTMLInputElement>(null);
  const creditsRef = useRef<HTMLInputElement>(null);
  const answerRef = useRef<HTMLElement>(null);

  const ask = (): void => {
    if (busy) return;
    setBusy(true);
    void runEstimate({ netSalary: net, commitments, deposit, durationMonths: duration }, door ?? unwired)
      .then(setOutcome)
      .finally(() => {
        setBusy(false);
      });
  };

  // Where the focus goes after an answer: the field that is missing, else the answer itself (the frame's keyboard walk).
  useEffect(() => {
    if (outcome === null) return;
    if (outcome.kind === "need") (outcome.missing === "netSalary" ? salaryRef : creditsRef).current?.focus();
    else if (outcome.kind === "refused") salaryRef.current?.focus();
    else answerRef.current?.focus();
  }, [outcome]);

  const salaryError =
    outcome?.kind === "need" && outcome.missing === "netSalary"
      ? t("dealer.estimation.manqueSalaire")
      : outcome?.kind === "refused"
        ? outcome.words
        : undefined;
  const creditsError =
    outcome?.kind === "need" && outcome.missing === "commitments"
      ? t("dealer.estimation.creditsEnChiffres")
      : undefined;

  return (
    <div className="flex flex-col gap-6" data-testid="estimation">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight" data-testid="estimation-titre">
          {t("dealer.estimation.titre")}
        </h1>
        <p className={`mt-1.5 text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.estimation.sousTitre")}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <Card>
          <h2 className="text-base font-semibold">{t("dealer.estimation.formTitre")}</h2>
          <form
            className="mt-4 flex flex-col gap-5"
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              ask();
            }}
          >
            <Field
              label={t("dealer.estimation.salaire")}
              htmlFor="estimation-salaire"
              {...(salaryError === undefined
                ? {}
                : { error: <span data-testid="estimation-salaire-erreur">{salaryError}</span> })}
            >
              <div className="flex items-center gap-2">
                <input
                  id="estimation-salaire"
                  ref={salaryRef}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="0"
                  value={net}
                  aria-invalid={salaryError === undefined ? undefined : true}
                  onChange={(event) => {
                    setNet(event.target.value);
                  }}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 font-mono text-sm"
                  data-testid="estimation-salaire"
                />
                <span className={`text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.estimation.unite")}</span>
              </div>
            </Field>

            <Field
              label={t("dealer.estimation.credits")}
              htmlFor="estimation-credits"
              hint={t("dealer.estimation.creditsAide")}
              {...(creditsError === undefined
                ? {}
                : { error: <span data-testid="estimation-credits-erreur">{creditsError}</span> })}
            >
              <div className="flex items-center gap-2">
                <input
                  id="estimation-credits"
                  ref={creditsRef}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="0"
                  value={commitments}
                  aria-invalid={creditsError === undefined ? undefined : true}
                  onChange={(event) => {
                    setCommitments(event.target.value);
                  }}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 font-mono text-sm"
                  data-testid="estimation-credits"
                />
                <span className={`text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.estimation.unite")}</span>
              </div>
            </Field>

            <div className="flex flex-col gap-2" data-testid="estimation-duree">
              <span className="text-sm font-medium text-gray-900">{t("dealer.estimation.duree")}</span>
              <Choice
                label={t("dealer.estimation.duree")}
                value={duration}
                options={DURATIONS.map((months) => ({
                  value: months,
                  label: fill("dealer.estimation.mois", { n: String(months) }),
                  testId: `estimation-duree-${String(months)}`,
                }))}
                onChange={setDuration}
              />
            </div>

            <div className="flex flex-col gap-2" data-testid="estimation-apport">
              <span className="text-sm font-medium text-gray-900">{t("dealer.estimation.apport")}</span>
              <Choice
                label={t("dealer.estimation.apport")}
                value={deposit}
                options={DEPOSITS.map((pct) => ({
                  value: pct,
                  label: fill("dealer.estimation.pourcent", { n: String(pct) }),
                  testId: `estimation-apport-${String(pct)}`,
                }))}
                onChange={setDeposit}
              />
            </div>

            <div className="flex flex-col gap-2">
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={busy}
                  className="inline-flex items-center gap-2 whitespace-nowrap"
                  data-testid="estimation-estimer"
                >
                  {t("dealer.estimation.estimer")}
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              <p className={`text-xs ${TEXTE_SECONDAIRE}`}>{t("dealer.estimation.aucunDocument")}</p>
            </div>
          </form>
        </Card>

        <div className="flex flex-col gap-6">
          <Answer
            outcome={outcome}
            answerRef={answerRef}
            net={net}
            onRetry={ask}
            onOpenVehicle={onOpenVehicle}
          />
        </div>
      </div>
    </div>
  );
}

function Notice(props: { readonly caveat: string }): React.JSX.Element {
  return (
    <p
      className="mt-4 flex items-start gap-2 rounded-xl bg-gray-50 px-3.5 py-2.5 text-sm text-gray-700"
      data-testid="estimation-caveat"
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" />
      <span>{props.caveat}</span>
    </p>
  );
}

function Answer(props: {
  readonly outcome: EstimateOutcome | null;
  readonly answerRef: React.RefObject<HTMLElement | null>;
  readonly net: string;
  readonly onRetry: () => void;
  readonly onOpenVehicle: (vehicleId: string) => void;
}): React.JSX.Element {
  const { outcome } = props;

  if (outcome === null || outcome.kind === "need" || outcome.kind === "refused") {
    return (
      <div data-testid="estimation-attente">
        <Card className="py-14 text-center">
          <h2 className="text-lg font-semibold">{t("dealer.estimation.attenteTitre")}</h2>
          <p className={`mt-1 text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.estimation.attenteTexte")}</p>
        </Card>
      </div>
    );
  }

  if (outcome.kind === "failed") {
    return (
      <ReadFailure
        what="dealer.lecture.estimation"
        message={t("dealer.estimation.echecPhrase")}
        onRetry={props.onRetry}
        testId="estimation-echec"
        retryTestId="estimation-reessayer"
      />
    );
  }

  if (outcome.kind === "no_capacity") {
    const view = outcome.view;
    return (
      <section
        ref={props.answerRef}
        tabIndex={-1}
        data-testid="estimation-sans-capacite"
        className="outline-none"
      >
        <Card>
          <Chip tone="attention">{t("dealer.estimation.chip")}</Chip>
          <h2 className="mt-3 text-xl font-semibold">{t("dealer.estimation.sansCapaciteTitre")}</h2>
          <p className="mt-2 text-sm text-gray-700">{t("dealer.estimation.sansCapaciteTexte")}</p>
          <div className="mt-4 text-sm" data-testid="estimation-changerait">
            <b>{t("dealer.estimation.changeraitTitre")}</b>
            <ul className="mt-1 list-disc pl-5 text-gray-700">
              <li>{t("dealer.estimation.changerait1")}</li>
              <li>{t("dealer.estimation.changerait2")}</li>
            </ul>
          </div>
          <Notice caveat={view.caveat} />
        </Card>
      </section>
    );
  }

  const view = outcome.view;
  const net = parseAmount(props.net);
  const bar = view.bar;
  const clamp = (share: number): number => Math.min(100, Math.max(0, share));
  const marginShare = Math.max(0, bar.monthlyMaxShare - bar.monthlyMinShare);
  return (
    <>
      <section ref={props.answerRef} tabIndex={-1} data-testid="estimation-resultat" className="outline-none">
        <Card>
          <Chip tone="attention">
            <span data-testid="estimation-chip">{t("dealer.estimation.chip")}</span>
          </Chip>
          <h2 className="mt-3 text-base font-semibold text-gray-700">{t("dealer.estimation.heroTitre")}</h2>
          <p
            className="mt-1 font-mono text-3xl font-bold"
            aria-label={fill("dealer.estimation.montantAlt", {
              min: view.financeable.min,
              max: view.financeable.max,
            })}
            data-testid="estimation-montant"
          >
            {rangeWords(view.financeable)}
          </p>

          <div className="mt-5">
            <p className="text-sm text-gray-700" data-testid="estimation-sur-salaire">
              {fill("dealer.estimation.surSalaire", {
                salaire: net === null ? "" : formatMoney(net),
              })}
            </p>
            <div
              role="img"
              aria-label={t("dealer.estimation.barreAlt")}
              className="relative mt-3 flex h-3 overflow-hidden rounded-full bg-gray-100"
              data-testid="estimation-barre"
            >
              <span
                className="h-full bg-gray-400"
                style={{ width: `${String(clamp(bar.commitmentsShare))}%` }}
              />
              <span
                className="h-full bg-indigo-600"
                style={{ width: `${String(clamp(bar.monthlyMinShare))}%` }}
              />
              <span className="h-full bg-indigo-200" style={{ width: `${String(clamp(marginShare))}%` }} />
              {bar.ceilingMarks.map((mark) => (
                <i
                  key={mark}
                  className="absolute top-0 h-full w-0.5 bg-gray-900"
                  style={{ left: `${String(clamp(mark))}%` }}
                  data-testid="estimation-plafond"
                />
              ))}
            </div>
            <p className={`mt-2 text-xs ${TEXTE_SECONDAIRE}`}>{t("dealer.estimation.barreNote")}</p>
            <p className="mt-1 text-sm font-semibold" data-testid="estimation-plafond-fourchette">
              {pctRange(view.ceiling)}
            </p>
            <ul className={`mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs ${TEXTE_SECONDAIRE}`}>
              <li>
                <i className="mr-1 inline-block h-2 w-2 rounded-full bg-gray-400" />
                {t("dealer.estimation.legCredits")}
              </li>
              <li>
                <i className="mr-1 inline-block h-2 w-2 rounded-full bg-indigo-600" />
                {t("dealer.estimation.legMensualite")}
              </li>
              <li>
                <i className="mr-1 inline-block h-2 w-2 rounded-full bg-indigo-200" />
                {t("dealer.estimation.legMarge")}
              </li>
            </ul>
          </div>

          <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div className="col-span-2">
              <dt className={TEXTE_SECONDAIRE}>{t("dealer.estimation.kvMensualite")}</dt>
              <dd className="font-mono" data-testid="estimation-mensualite">
                {rangeWords(view.monthly)}
              </dd>
            </div>
            <div>
              <dt className={TEXTE_SECONDAIRE}>{t("dealer.estimation.kvDuree")}</dt>
              <dd data-testid="estimation-duree-servie">
                {fill("dealer.estimation.mois", { n: String(view.durationMonths) })}
              </dd>
            </div>
            <div>
              <dt className={TEXTE_SECONDAIRE}>{t("dealer.estimation.kvApport")}</dt>
              <dd data-testid="estimation-apport-servi">
                {fill("dealer.estimation.pourcent", { n: String(view.depositPct) })}
              </dd>
            </div>
          </dl>

          <div className="mt-5" data-testid="estimation-hypotheses">
            <h3 className="text-sm font-semibold text-gray-700">{t("dealer.estimation.hypotheses")}</h3>
            <dl className="mt-1 flex flex-col gap-1 text-sm">
              {view.assumptions.map((line) => (
                <div key={line.key} className="flex flex-wrap gap-x-2">
                  <dt className={TEXTE_SECONDAIRE}>{line.label}</dt>
                  <dd>{line.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <Notice caveat={view.caveat} />
        </Card>
      </section>

      <section data-testid="estimation-voitures">
        <h2 className="mb-3 flex items-center gap-2 text-base font-semibold">
          {t("dealer.estimation.voituresTitre")}
          {outcome.kind === "estimate" ? <Chip>{String(outcome.view.cars.within.length)}</Chip> : null}
        </h2>
        {outcome.kind === "estimate" ? (
          <div className="flex flex-col gap-3">
            {outcome.view.cars.within.map((car) => (
              <article
                key={car.vehicleId}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3"
                data-testid={`estimation-voiture-${car.vehicleId}`}
              >
                <button
                  type="button"
                  className="min-w-0 text-left"
                  aria-label={fill("dealer.estimation.voirFiche", { titre: car.title })}
                  data-testid={`estimation-voiture-${car.vehicleId}-lien`}
                  onClick={() => {
                    props.onOpenVehicle(car.vehicleId);
                  }}
                >
                  <b className="block truncate">{car.title}</b>
                  <span className="font-mono text-sm text-gray-700">{car.price}</span>
                </button>
                <dl className="grid w-full grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div>
                    <dt className={TEXTE_SECONDAIRE}>{t("dealer.estimation.kvMensualite")}</dt>
                    <dd className="font-mono" data-testid={`estimation-voiture-${car.vehicleId}-mensualite`}>
                      {rangeWords(car.monthly)}
                    </dd>
                  </div>
                  <div>
                    <dt className={TEXTE_SECONDAIRE}>{t("dealer.estimation.resteAVivre")}</dt>
                    <dd className="font-mono" data-testid={`estimation-voiture-${car.vehicleId}-reste`}>
                      {rangeWords(car.resteAVivre)}
                    </dd>
                  </div>
                  <div className="col-span-2 flex items-center gap-2">
                    <i
                      aria-hidden="true"
                      className={`inline-block h-2 w-2 rounded-full ${BAND_DOT[car.effortBand]}`}
                    />
                    <span
                      data-band={car.effortBand}
                      data-testid={`estimation-voiture-${car.vehicleId}-bande`}
                    >
                      {car.effortBandLabel}
                    </span>
                  </div>
                </dl>
                <GuardedButton
                  disabled
                  reason={t("dealer.estimation.dossierAssisteRaison")}
                  variant="secondary"
                  testId={`estimation-voiture-${car.vehicleId}-assiste`}
                >
                  {t("dealer.estimation.dossierAssiste")}
                </GuardedButton>
              </article>
            ))}
          </div>
        ) : outcome.view.cars.kind === "empty_park" ? (
          <div data-testid="estimation-parc-vide">
            <Card className="text-center">
              <h3 className="font-semibold">{t("dealer.estimation.parcVide")}</h3>
              <p className={`mt-1 text-sm ${TEXTE_SECONDAIRE}`}>{t("dealer.estimation.parcVideTexte")}</p>
            </Card>
          </div>
        ) : (
          <div data-testid="estimation-aucune-voiture">
            <Card className="text-center">
              <h3 className="font-semibold">{t("dealer.estimation.aucuneVoiture")}</h3>
              <p className={`mt-1 text-sm ${TEXTE_SECONDAIRE}`}>
                {t("dealer.estimation.aucuneVoitureTexte")}
              </p>
            </Card>
          </div>
        )}
        {view.withoutRate.length > 0 ? (
          <ul className="mt-3 flex flex-col gap-3">
            {view.withoutRate.map((car) => (
              <li
                key={car.vehicleId}
                className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3"
                data-testid={`estimation-sans-taux-${car.vehicleId}`}
              >
                <b className="block truncate">{car.title}</b>
                <span className="font-mono text-sm text-gray-700">{car.price}</span>
                <p className={`mt-1 text-sm ${TEXTE_SECONDAIRE}`}>{car.sentence}</p>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </>
  );
}
